import { createAsyncThunk } from "@reduxjs/toolkit";
import { Dispatch } from "react";

import { EventGuestlistBlockService } from "Bands/services";
import { EventDetailsExportService } from "Bands/services/EventDetailsExportService";
import {
  BAND_ACTIONS,
  IEventDetailsExportParams,
  IEventGuestParams,
  IEventGuestStatus,
  IGuestlistBlockParams,
  IGuestlistTicketAndPassParams,
} from "Bands/types";
import { API_ENDPOINTS } from "shared/constants";
import { IDataKeys } from "shared/types";
import { getIdFromPath } from "shared/utils";

export const dispatchGetEventGuestlistBlocks = (
  dispatch: Dispatch<any>,
  event?: string
) => {
  dispatch(
    getEventGuestlistBlocks({
      event,
      page: 1, // we assume all blocks will be returned in page 1
      itemsPerPage: 100, // avoid pagination
    })
  );
};

export const getEventGuestlistBlocks = createAsyncThunk(
  BAND_ACTIONS.getEventGuestlistBlocks,
  async (
    {
      event,
      itemsPerPage,
      page,
    }: {
      event?: string;
      page: number;
      itemsPerPage: number;
    },
    { rejectWithValue }
  ) => {
    try {
      const data = await EventGuestlistBlockService.getBlocks({
        event: event ? API_ENDPOINTS.EVENT(event) : undefined,
        page,
        itemsPerPage,
      });

      // keep the key even if response is empty
      return {
        [IDataKeys.eventGuestListBlock]: data.eventGuestListBlock,
        meta: data.meta,
        pagination: data.pagination,
      };
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const dispatchGetEventGuestlistBlock = (
  dispatch: Dispatch<any>,
  blockId: string
) => {
  const action = API_ENDPOINTS.EVENT_GUESTLIST_BLOCK(blockId);

  return dispatch(getEventGuestlistBlock({ blockId, action }));
};

export const getEventGuestlistBlock = createAsyncThunk(
  BAND_ACTIONS.getEventGuestlistBlock,
  async (
    { blockId }: { blockId: string; action: string },
    { rejectWithValue, dispatch }
  ) => {
    try {
      const block = await EventGuestlistBlockService.getBlock(blockId);

      return block;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const createEventGuestlistBlock = createAsyncThunk(
  BAND_ACTIONS.createEventGuestlistBlock,
  async (block: IGuestlistBlockParams, { rejectWithValue }) => {
    try {
      const data = await EventGuestlistBlockService.createBlock(block);
      const id = Object.values(data?.eventGuestListBlock)?.[0]?.attributes?.id;
      return { id, data };
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const editEventGuestlistBlockTitle = createAsyncThunk(
  BAND_ACTIONS.editEventGuestlistBlockTitle,
  async (props: { id: string; title: string }, { rejectWithValue }) => {
    try {
      const data = await EventGuestlistBlockService.editBlockTitle(props);
      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const editEventGuestlistBlock = createAsyncThunk(
  BAND_ACTIONS.editEventGuestlistBlock,
  async (
    props: { id: string; data: IGuestlistBlockParams },
    { rejectWithValue }
  ) => {
    try {
      const data = await EventGuestlistBlockService.editBlock(props);
      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const deleteEventGuestlistBlock = createAsyncThunk(
  BAND_ACTIONS.deleteEventGuestlistBlock,
  async (id: string, { rejectWithValue }) => {
    try {
      await EventGuestlistBlockService.deleteBlock(id);
      return {
        path: API_ENDPOINTS.EVENT_GUESTLIST_BLOCK(id),
        dataKey: IDataKeys.eventGuestListBlock,
      };
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const createEventTicketAndPass = createAsyncThunk(
  BAND_ACTIONS.createEventTicketAndPass,
  async (
    ticketAndPass: IGuestlistTicketAndPassParams,
    { rejectWithValue, dispatch }
  ) => {
    try {
      const data = await EventGuestlistBlockService.createTicketAndPass(
        ticketAndPass
      );

      // refetch block on create/edit/delete ticket&type
      if (ticketAndPass.guestListBlock) {
        dispatchGetEventGuestlistBlock(
          dispatch,
          getIdFromPath(
            ticketAndPass.guestListBlock,
            API_ENDPOINTS.EVENT_GUESTLIST_BLOCKS
          )
        );
      }

      const id = Object.values(data?.eventGuestListTicketAndPassType)?.[0]
        ?.attributes?.id;

      return { id, data };
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const editEventTicketAndPass = createAsyncThunk(
  BAND_ACTIONS.editEventTicketAndPass,
  async (
    {
      blockId,
      ...props
    }: { id: string; data: IGuestlistTicketAndPassParams; blockId: string },
    { rejectWithValue, dispatch }
  ) => {
    try {
      const data = await EventGuestlistBlockService.editTicketAndPass(props);

      // refetch block on create/edit/delete ticket&type
      dispatchGetEventGuestlistBlock(dispatch, blockId);

      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const deleteEventTicketAndPass = createAsyncThunk(
  BAND_ACTIONS.deleteEventTicketAndPass,
  async (
    { id, blockId }: { id: string; blockId: string },
    { rejectWithValue, dispatch }
  ) => {
    try {
      await EventGuestlistBlockService.deleteTicketAndPass(id);

      // refetch block on create/edit/delete ticket&type
      dispatchGetEventGuestlistBlock(dispatch, blockId);

      return {
        path: API_ENDPOINTS.EVENT_TICKET_PASS(id),
        dataKey: IDataKeys.eventGuestListTicketAndPassType,
      };
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const dispatchGetEventGuests = ({
  dispatch,
  blockId,
  page,
  isRecursive,
}: {
  dispatch: Dispatch<any>;
  blockId: string;
  page?: number;
  isRecursive?: boolean;
}) => {
  dispatch(
    getEventGuests({
      blockId,
      page,
      paginationKey: blockId
        ? API_ENDPOINTS.EVENT_GUESTLIST_BLOCK(blockId)
        : API_ENDPOINTS.EVENT_GUESTLIST_BLOCKS,
      isRecursive,
    })
  );
};

export const getEventGuests = createAsyncThunk(
  BAND_ACTIONS.getEventGuests,
  async (
    {
      blockId,
      status,
      page,
      isRecursive,
    }: {
      blockId: string;
      status?: IEventGuestStatus;
      page?: number;
      paginationKey?: string;
      isRecursive?: boolean;
    },
    { rejectWithValue }
  ) => {
    try {
      const data = await EventGuestlistBlockService.getGuests(
        {
          guestListBlock: blockId
            ? API_ENDPOINTS.EVENT_GUESTLIST_BLOCK(blockId)
            : undefined,
          page,
          status,
        },
        isRecursive
      );

      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const createEventGuest = createAsyncThunk(
  BAND_ACTIONS.createEventGuest,
  async (guest: IEventGuestParams, { rejectWithValue, dispatch }) => {
    try {
      const data = await EventGuestlistBlockService.createGuest(guest);

      // refetch block on create/edit/delete guest
      if (guest.guestListBlock) {
        dispatchGetEventGuestlistBlock(
          dispatch,
          getIdFromPath(
            guest.guestListBlock,
            API_ENDPOINTS.EVENT_GUESTLIST_BLOCKS
          )
        );
      }

      const id = Object.values(data?.eventGuestListBlockEntry)?.[0]?.attributes
        ?.id;

      return {
        id,
        data: {
          ...data,
          guestListBlock: guest.guestListBlock,
        },
      };
    } catch (error) {
      // refetch block on add guest error (eg. not enough tickets)
      if (guest.guestListBlock) {
        dispatchGetEventGuestlistBlock(
          dispatch,
          getIdFromPath(
            guest.guestListBlock,
            API_ENDPOINTS.EVENT_GUESTLIST_BLOCKS
          )
        );
      }

      return rejectWithValue(error);
    }
  }
);

export const editEventGuest = createAsyncThunk(
  BAND_ACTIONS.editEventGuest,
  async (
    {
      blockId,
      ...props
    }: { id: string; data: IEventGuestParams; blockId: string },
    { dispatch, rejectWithValue }
  ) => {
    try {
      const data = await EventGuestlistBlockService.editGuest(props);

      // refetch block on create/edit/delete guest
      dispatchGetEventGuestlistBlock(dispatch, blockId);

      return data;
    } catch (error) {
      // refetch block on edit guest error (eg. not enough tickets)
      dispatchGetEventGuestlistBlock(dispatch, blockId);

      return rejectWithValue(error);
    }
  }
);

export const deleteEventGuest = createAsyncThunk(
  BAND_ACTIONS.deleteEventGuest,
  async (
    { id, blockId }: { id: string; blockId: string },
    { rejectWithValue, dispatch }
  ) => {
    try {
      await EventGuestlistBlockService.deleteGuest(id);

      // refetch block on create/edit/delete guest
      dispatchGetEventGuestlistBlock(dispatch, blockId);

      return {
        path: API_ENDPOINTS.EVENT_GUEST(id),
        dataKey: IDataKeys.eventGuestListBlockEntry,
      };
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const downloadEventGuestlistBlock = createAsyncThunk(
  BAND_ACTIONS.downloadEventGuestlistBlock,
  async (
    {
      id,
      printNote,
      blockId,
    }: { id: string; printNote?: string; blockId: string },
    { rejectWithValue, dispatch }
  ) => {
    try {
      const data = await EventGuestlistBlockService.downloadBlock(
        id,
        printNote
      );

      // refetch block on download guestlist
      dispatchGetEventGuestlistBlock(dispatch, blockId);

      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const mailGuestlist = createAsyncThunk(
  BAND_ACTIONS.createEventDetailsExport,
  async (
    { data, blockId }: { data: IEventDetailsExportParams; blockId: string },
    { rejectWithValue, dispatch }
  ) => {
    try {
      const createdItem =
        await EventDetailsExportService.createEventDetailsExport({
          ...data,
          paperSize: "a4",
          showMyPersonalInfo: false,
        });

      const id = Object.values(createdItem?.eventDetailsExport)?.[0]?.attributes
        ?.id;

      // refetch block on email guestlist
      dispatchGetEventGuestlistBlock(dispatch, blockId);

      return { id };
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const dispatchGetLastGuestlistSent = (
  dispatch: Dispatch<any>,
  exportId: string
) => {
  dispatch(
    getLastGuestlistSent(
      getIdFromPath(exportId, API_ENDPOINTS.EVENT_DETAILS_EXPORT)
    )
  );
};

export const getLastGuestlistSent = createAsyncThunk(
  BAND_ACTIONS.getLastGuestlistSent,
  async (id: string, { rejectWithValue }) => {
    try {
      const data = await EventDetailsExportService.getLastGuestlistSent(id);

      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);
